/**
 * Заметки сотрудников по организации. Простая лента markdown-карточек:
 *  - Создание (textarea + Save)
 *  - Редактирование (inline)
 *  - Удаление с подтверждением
 *  - К каждой заметке — компонент Attachments (owner_type='note', owner_id=note.id)
 *
 * Видна только staff (компонент монтируется только для staff).
 */
import { useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Save, X, FileText } from 'lucide-react';
import { orgNotesApi, type OrgNote } from '../api/org_notes';
import { Markdown } from '../lib/markdown';
import { Attachments } from './Attachments';
import { fmtTime } from '../lib/format';

export default function OrgNotesPanel({ orgId }: { orgId: string }) {
  const [items, setItems] = useState<OrgNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await orgNotesApi.list(orgId)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [orgId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setCreating(true);
    try {
      await orgNotesApi.create(orgId, draft);
      setDraft('');
      await load();
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-3">
      <form onSubmit={submit} className="space-y-2">
        <textarea value={draft} onChange={e => setDraft(e.target.value)}
          rows={3} placeholder="Записать факт о клиенте: контактах, привычках, договорённостях…"
          className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
        <button type="submit" disabled={creating || !draft.trim()}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1.5 rounded text-sm font-bold">
          {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          Добавить заметку
        </button>
      </form>

      {loading ? (
        <Loader2 className="animate-spin text-blue-400 mx-auto my-4" />
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-4">Заметок пока нет.</div>
      ) : items.map(n => (
        <NoteCard key={n.id} note={n} orgId={orgId}
          editing={editing === n.id}
          onStartEdit={() => setEditing(n.id)}
          onCancelEdit={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={load} />
      ))}
    </div>
  );
}

function NoteCard({ note, orgId, editing, onStartEdit, onCancelEdit, onSaved, onDeleted }:
  { note: OrgNote; orgId: string; editing: boolean;
    onStartEdit: () => void; onCancelEdit: () => void;
    onSaved: () => void; onDeleted: () => void }) {
  const [text, setText] = useState(note.body_md);
  const [busy, setBusy] = useState(false);
  const [showAttach, setShowAttach] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await orgNotesApi.update(orgId, note.id, text);
      onSaved();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Удалить заметку?')) return;
    setBusy(true);
    try { await orgNotesApi.delete(orgId, note.id); onDeleted(); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
      <header className="flex items-baseline justify-between text-[10px] text-slate-500 mb-1">
        <span>
          <b className="text-slate-300">{note.author_name || '—'}</b> · {fmtTime(note.created_at)}
          {note.updated_at !== note.created_at && ' · ред.'}
        </span>
        {!editing && (
          <div className="flex items-center gap-1">
            <button onClick={() => setShowAttach(s => !s)} title="Вложения"
              className="text-slate-500 hover:text-blue-400 p-1">
              <FileText size={12} />
            </button>
            <button onClick={onStartEdit} title="Редактировать"
              className="text-slate-500 hover:text-blue-400 p-1">
              <Pencil size={12} />
            </button>
            <button onClick={remove} disabled={busy} title="Удалить"
              className="text-slate-500 hover:text-red-400 p-1">
              <Trash2 size={12} />
            </button>
          </div>
        )}
      </header>
      {editing ? (
        <form onSubmit={save} className="space-y-2">
          <textarea value={text} onChange={e => setText(e.target.value)} rows={4} autoFocus
            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white" />
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !text.trim()}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1 rounded text-xs">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
              Сохранить
            </button>
            <button type="button" onClick={onCancelEdit}
              className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-300 px-3 py-1 rounded text-xs">
              <X size={11} /> Отмена
            </button>
          </div>
        </form>
      ) : (
        <div className="text-sm text-slate-200 prose prose-sm prose-invert max-w-none">
          <Markdown source={note.body_md} />
        </div>
      )}
      {showAttach && !editing && (
        <div className="mt-3 pt-3 border-t border-slate-800">
          <Attachments ownerType="note" ownerId={note.id} />
        </div>
      )}
    </div>
  );
}
